import { logger } from "@/lib/logger";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface FeeScheduleInput {
  fee_name: string;
  fee_description: string;
  fee_category: string;
  amount: number;
  event_trigger_type: string;
}

interface SaveProtocolConfigurationParams {
  trialId: string;
  feeSchedules: FeeScheduleInput[];
}

export function useProtocolConfiguration() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const saveProtocolConfiguration = useMutation({
    mutationFn: async ({ trialId, feeSchedules }: SaveProtocolConfigurationParams) => {
      // Insert all fee schedules in bulk
      const schedules = feeSchedules.map(schedule => ({
        trial_id: trialId,
        fee_name: schedule.fee_name,
        fee_description: schedule.fee_description,
        fee_category: schedule.fee_category,
        amount: schedule.amount,
        currency: "USD",
        is_active: true,
        effective_from: new Date().toISOString(),
        event_trigger_type: schedule.event_trigger_type,
      }));

      const { data, error } = await supabase
        .from("protocol_fee_schedules")
        .insert(schedules)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["protocol-fee-schedules"] });
    },
    onError: (error: Error) => {
      logger.error("Error saving protocol configuration:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    saveProtocolConfiguration: saveProtocolConfiguration.mutate,
    isSaving: saveProtocolConfiguration.isPending,
  };
}
